import {
  PublicAccountPaymentMethod,
  type PublicAccountItem,
} from "../types/public-accounts-api.types";
import type {
  AccountCardDisplay,
  AccountSectionDisplay,
} from "../types/accounts-wallets.types";

const COUNTRY_CODE_TO_NAME: Record<string, string> = {
  US: "United States",
  USA: "United States",
  CO: "Colombia",
  COL: "Colombia",
  BR: "Brazil",
  BRA: "Brazil",
  MX: "México",
  MEX: "México",
  AR: "Argentina",
  ARG: "Argentina",
  CL: "Chile",
  CHL: "Chile",
  PE: "Perú",
  PER: "Perú",
  PY: "Paraguay",
  PRY: "Paraguay",
  UY: "Uruguay",
  URY: "Uruguay",
  CN: "China",
  CHN: "China",
  HK: "Hong Kong",
  HKG: "Hong Kong",
  CR: "Costa Rica",
  CRI: "Costa Rica",
  GT: "Guatemala",
  GTM: "Guatemala",
  EC: "Ecuador",
  ECU: "Ecuador",
  PA: "Panamá",
  PAN: "Panamá",
  USDT: "USDT",
  USDC: "USDC - Activo digital",
};

function getSectionTitle(countryCode: string, isDigital: boolean): string {
  if (isDigital) return "Digital Assets";
  const upper = countryCode.toUpperCase();
  return COUNTRY_CODE_TO_NAME[upper] ?? countryCode;
}

function getBankName(structured_data?: Record<string, unknown> | null): string {
  if (!structured_data || typeof structured_data !== "object") return "—";
  const name =
    (structured_data.bank_name as string) ??
    (structured_data.bankName as string) ??
    (structured_data.institution_name as string);
  return typeof name === "string" ? name : "—";
}

/** Crypto wallet network (TRC20, BEP20, etc.); equivalent to bank name for bank accounts. */
function getNetworkName(structured_data?: Record<string, unknown> | null): string {
  if (!structured_data || typeof structured_data !== "object") return "—";
  const network =
    (structured_data.network as string) ??
    (structured_data.network_name as string);
  return typeof network === "string" && network.trim() ? network.trim() : "—";
}

function getMaskedNumber(
  structured_data?: Record<string, unknown> | null,
  paymentMethod: PublicAccountPaymentMethod = PublicAccountPaymentMethod.BANK
): string {
  if (!structured_data || typeof structured_data !== "object") {
    return paymentMethod === PublicAccountPaymentMethod.CRYPTO_WALLET
      ? "—"
      : "**** —";
  }
  const last4 =
    (structured_data.account_number_last4 as string) ??
    (structured_data.last_four as string) ??
    (structured_data.last4 as string);
  if (typeof last4 === "string" && last4.length > 0) return `**** ${last4}`;
  const fullNumber = structured_data.account_number as string;
  if (typeof fullNumber === "string" && fullNumber.length >= 4) {
    return `**** ${fullNumber.slice(-4)}`;
  }
  const masked = structured_data.masked_number as string;
  if (typeof masked === "string") return masked;
  const address = structured_data.wallet_address as string;
  if (typeof address === "string" && address.length > 10) {
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  }
  return paymentMethod === PublicAccountPaymentMethod.CRYPTO_WALLET
    ? "—"
    : "**** —";
}

export function mapPublicAccountToDisplay(item: PublicAccountItem): AccountCardDisplay {
  const isDigital =
    item.payment_method === PublicAccountPaymentMethod.CRYPTO_WALLET;
  const kind = isDigital ? "digital" : "bank";
  const bankName = isDigital
    ? getNetworkName(item.structured_data)
    : getBankName(item.structured_data);
  const maskedNumber = getMaskedNumber(
    item.structured_data,
    item.payment_method
  );
  const attachmentsCount = item.attachment_urls?.length ?? 0;

  return {
    id: item.id,
    currency: item.currency,
    name: item.internal_alias?.trim() || "Cuenta",
    bankName,
    maskedNumber,
    description: item.notes?.trim() || null,
    attachmentsCount,
    isVerified: false,
    kind,
    extraLabel: isDigital ? "Multi-sig Required" : null,
    countryCode: isDigital ? "digital" : (item.country_code?.trim() ?? null),
  };
}

export function groupPublicAccountsIntoSections(
  items: PublicAccountItem[]
): AccountSectionDisplay[] {
  const digital: PublicAccountItem[] = [];
  const byCountry = new Map<string, PublicAccountItem[]>();

  for (const item of items) {
    if (item.payment_method === PublicAccountPaymentMethod.CRYPTO_WALLET) {
      digital.push(item);
    } else {
      const code = item.country_code?.trim() || "OTHER";
      const upper = code.toUpperCase();
      if (!byCountry.has(upper)) byCountry.set(upper, []);
      byCountry.get(upper)!.push(item);
    }
  }

  const sections: AccountSectionDisplay[] = [];

  for (const [code, accounts] of byCountry.entries()) {
    sections.push({
      id: code.toLowerCase(),
      title: getSectionTitle(code, false),
      code,
      accountCount: accounts.length,
      isDigital: false,
      accounts: accounts.map(mapPublicAccountToDisplay),
    });
  }

  if (digital.length > 0) {
    sections.push({
      id: "digital",
      title: "Digital Assets",
      code: "digital",
      accountCount: digital.length,
      isDigital: true,
      accounts: digital.map(mapPublicAccountToDisplay),
    });
  }

  return sections.sort((a, b) => {
    if (a.id === "digital") return 1;
    if (b.id === "digital") return -1;
    return a.title.localeCompare(b.title);
  });
}
